/* eslint-disable */
import { createSlice } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import { toast } from "react-toastify";

import type { TSanitizationState } from "../../types/sanitization.type";
import {
	buildFallbackAvailableColsBySource,
	flattenAvailableCols,
	groupAvailableColsBySource,
} from "../../utils/availableColumnsUtils";

import { type StepEnum } from "../../pages/SanitizeTape";
import { getSanitizationSteps } from "../../utils/tapeUtils";

import {
	addCustomColumnThunk,
	deleteCustomColumnThunk,
	editTapeMetaDataThunk,
	getColumnMappingDataThunk,
	getCustomColumnsThunk,
	getDataFromDataRange,
	getDataRangeThunk,
	getPreferencesThunk,
	getSearchMeta,
	getStatistics,
	getTapeInfo,
	updateCustomColumnThunk,
	updateColumnMappingDataThunk,
	uploadTapeThunk,
} from "./sanitization.thunk";

const inferDatatype = (description: string): string => {
	const desc = description.toLowerCase();
	if (
		desc.includes("verified") ||
		desc.includes("is_") ||
		desc.includes("has_") ||
		desc.includes("ever_")
	) {
		return "bool";
	}
	if (
		desc.includes("date") ||
		desc.includes("time") ||
		desc.includes("month") ||
		desc.includes("year") ||
		desc.includes("quarter")
	) {
		return "date";
	}
	if (
		desc.includes("utilization") ||
		desc.includes("inquiries") ||
		desc.includes("lines") ||
		desc.includes("balance") ||
		desc.includes("delinq") ||
		desc.includes("pti") ||
		desc.includes("dti") ||
		desc.includes("amt") ||
		desc.includes("age") ||
		desc.includes("length") ||
		desc.includes("percent") ||
		desc.includes("ratio") ||
		desc.includes("revolving") ||
		desc.includes("bks") ||
		desc.includes("number") ||
		desc.includes("count")
	) {
		return "float";
	}
	return "str";
};

const mapCustomColumnResponse = (col: any) => {
	const columnMetadata = col?.column_metadata ?? {};
	const displayNameRaw = String(
		columnMetadata?.display_name ?? ""
	).trim();
	const displayNameWithoutSuffix = displayNameRaw
		.split(" (")[0]
		.trim();
	const transformDescriptionInputType =
		col?.transform_description === "sql_code" ||
		col?.transform_description === "sql"
			? "sql_code"
			: col?.transform_description === "python_code" ||
			    col?.transform_description === "python"
			  ? "python_code"
			  : undefined;
	const sqlTransformationCode = col?.sql_transformation_code ?? "";
	const pythonTransformationCode =
		col?.python_transformation_code ?? "";
	const resolvedInputType =
		col?.user_input_type ??
		transformDescriptionInputType ??
		(sqlTransformationCode
			? "sql_code"
			: pythonTransformationCode
			  ? "python_code"
			  : "python_code");

	return {
		col_id: col?.column ?? col?.col_id ?? "",
		name:
			displayNameWithoutSuffix ||
			columnMetadata?.description ||
			"Unnamed Column",
		description: columnMetadata?.description ?? "",
		datatype:
			columnMetadata?.datatype ??
			inferDatatype(columnMetadata?.description ?? ""),
		user_input:
			col?.user_input ??
			(sqlTransformationCode || pythonTransformationCode || ""),
		user_input_type: resolvedInputType,
		is_user_confirmed: col?.is_user_confirmed ?? false,
		is_default_custom_column:
			col?.is_default_custom_column ?? false,
		is_valid_code: col?.is_valid_code ?? false,
		logs: col?.logs ?? [],
		prompts: col?.prompts ?? [],
		color: columnMetadata?.color ?? "yellow",
		initial_values: col?.initial_values ?? null,
		final_values: col?.final_values ?? null,
	};
};

const findCustomColumnIndex = (
	columns: Array<any>,
	targetColumn: any,
	{
		colId,
		name,
	}: {
		colId?: string;
		name?: string;
	}
) => {
	const targetColId = colId ?? targetColumn?.col_id;
	if (targetColId) {
		return columns.findIndex(
			(column: any) => column.col_id === targetColId
		);
	}

	const normalizedName = (
		name ??
		targetColumn?.name ??
		""
	)
		.trim()
		.toLowerCase();
	if (!normalizedName) {
		return -1;
	}

	return columns.findIndex(
		(column: any) =>
			String(column?.name ?? "")
				.trim()
				.toLowerCase() === normalizedName
	);
};

const applyCustomColumnsStateFromResponse = (
	customColumnsState: any,
	payload: any,
	tapeId: string | null,
	options?: {
		mergeSingleColumn?: boolean;
		colId?: string;
		name?: string;
	}
) => {
	const responseColumns =
		payload?.custom_columns ??
		payload?.user_defined_cols ??
		[];
	const transformedColumns = responseColumns.map(
		(col: any) => mapCustomColumnResponse(col)
	);
	const rawColsFromResponse =
		payload?.raw_cols?.all_cols ??
		payload?.raw_cols ??
		payload?.all_cols ??
		customColumnsState.rawCols;
	const normalizedRawCols = Array.isArray(rawColsFromResponse)
		? rawColsFromResponse
		: [];
	const rawColsBySource = groupAvailableColsBySource(
		payload?.available_cols
	);

	if (
		options?.mergeSingleColumn &&
		transformedColumns.length === 1
	) {
		const nextColumns = [...customColumnsState.columns];
		const targetColumn = transformedColumns[0];
		const existingIndex = findCustomColumnIndex(
			nextColumns,
			targetColumn,
			{
				colId: options.colId,
				name: options.name,
			}
		);

		if (existingIndex >= 0) {
			nextColumns[existingIndex] = targetColumn;
		} else {
			nextColumns.push(targetColumn);
		}
		customColumnsState.columns = nextColumns;
	} else if (transformedColumns.length > 0 || !options?.mergeSingleColumn) {
		customColumnsState.columns = transformedColumns;
	}
	customColumnsState.rawCols = normalizedRawCols;
	customColumnsState.rawColsBySource =
		Object.keys(rawColsBySource).length > 0
			? rawColsBySource
			: buildFallbackAvailableColsBySource(
					normalizedRawCols
				);
	customColumnsState.tapeId = tapeId;
	customColumnsState.hasFetched = true;
	customColumnsState.loading = false;
	customColumnsState.error = null;
};

const getColumnMappingColumns = (payload: any): Array<any> => {
	if (Array.isArray(payload)) {
		return payload;
	}
	if (Array.isArray(payload?.user_defined_cols)) {
		return payload.user_defined_cols;
	}
	if (Array.isArray(payload?.allColumns)) {
		return payload.allColumns;
	}
	return [];
};

const getColumnMappingAvailableColsBySource = (
	payload: any
): Record<string, Array<string>> => {
	const groupedAvailableCols = groupAvailableColsBySource(
		payload?.available_cols
	);
	if (Object.keys(groupedAvailableCols).length > 0) {
		return groupedAvailableCols;
	}

	if (Array.isArray(payload?.raw_cols?.all_cols)) {
		return buildFallbackAvailableColsBySource(
			payload.raw_cols.all_cols
		);
	}
	if (Array.isArray(payload?.raw_cols)) {
		return buildFallbackAvailableColsBySource(payload.raw_cols);
	}
	return {};
};

const getColumnMappingAvailableCols = (payload: any): Array<string> => {
	if (Array.isArray(payload?.availableCols)) {
		return payload.availableCols;
	}

	const flattenedAvailableCols = flattenAvailableCols(
		payload?.available_cols
	);
	if (flattenedAvailableCols.length > 0) {
		return flattenedAvailableCols;
	}

	if (Array.isArray(payload?.raw_cols?.all_cols)) {
		return payload.raw_cols.all_cols;
	}
	if (Array.isArray(payload?.raw_cols)) {
		return payload.raw_cols;
	}
	return [];
};

const mapColumnMappingState = (payload: any) => {
	return {
		allColumns: getColumnMappingColumns(payload),
		availableCols: getColumnMappingAvailableCols(payload),
		availableColsBySource:
			getColumnMappingAvailableColsBySource(payload),
	};
};

const initialState: TSanitizationState = {
	activeTapeIdForSanitization: null,
	isLoading: false,
	error: null,
	searchMeta: null,
	preferences: {
		showDataRangeCoachMark: false,
		showColumnMappingCoachMark: false,
	},
	tapeUploadStatus: {
		status: "not-started",
		error: null,
		loading: false,
	},
	tapeSanitizationInfoStatus: {
		loading: false,
		error: null,
	},
	tapeSanitizationInfo: null,
	tapeExtractionStatus: {
		loading: false,
		error: null,
		shortPreviewLoading: false,
	},
	tapeExtraction: null,
	columnMappingStatus: {
		loading: false,
		error: null,
		previewLoading: false,
	},
	columnMapping: null,
	customColumns: {
		columns: [],
		rawCols: [],
		rawColsBySource: {},
		loading: false,
		error: null,
		saveLoading: false,
		tapeId: null,
		hasFetched: false,
	},
	reviewStatisticsStatus: {
		loading: false,
		error: null,
	},
	reviewStatistics: null,
	nextStep: null,
	submissionStatus: null,
	tapeDownloadData: null,
};

export const sanitizationState = createSlice({
	name: "sanitization",
	initialState,
	reducers: {
		resetEverything: (state) => {
			state = initialState;
		},

		updateTapeUploadStatus: (state, action) => {
			state.tapeUploadStatus = action.payload;
		},

		updateRangeSelection: (state, action) => {
			const { sheet, headerRange, dataRange } =
				action.payload;
			if (state.tapeExtraction) {
				state.tapeExtraction.recentRangeSelection = {
					sheet,
					headerRange,
					dataRange,
				};
			}
		},
		updateCurrentStep: (state, action) => {
			if (state.tapeSanitizationInfo) {
				const currentStepIndex =
					action.payload.all_steps.findIndex(
						(el: string) =>
							el === action.payload.current_step
					);
				state.tapeSanitizationInfo = {
					allSteps:
						action.payload.all_steps.map(
							(el: string, index: number) => {
								const step = getSanitizationSteps(el);
								return {
									...step,
									isCompleted: currentStepIndex > index,
								};
							}
						) ?? [],
					currentStep: {
						...getSanitizationSteps(
							action.payload.current_step as string
						),
						isCompleted: false,
					},
					tapeName: action.payload.tape_name,
				};
				state.submissionStatus = {
					currentStep:
						state.tapeSanitizationInfo.currentStep.path,
					nextStep: state.tapeSanitizationInfo.currentStep
						.nextStep as StepEnum,
					submissionCreated: false,
					isSubmissionSuccessful: false,
					error: null,
				};
			}
		},
		updateSanitizationSubmissionStatus: (state, action) => {
			state.submissionStatus = action.payload;
		},
		updateColumnMappingData: (state, action) => {
			state.columnMapping =
				mapColumnMappingState(action.payload);
		},
		updatePreferences: (state, action) => {
			state.preferences = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(uploadTapeThunk.pending, (state) => {
				state.tapeUploadStatus.status = "in-progress";
				state.tapeUploadStatus.loading = true;
				state.error = null;
			})
			.addCase(
				uploadTapeThunk.fulfilled,
				(state, action) => {
					const payload = action.payload;
					state.tapeUploadStatus.status = "completed";
					state.tapeUploadStatus.loading = false;
					state.activeTapeIdForSanitization =
						payload.tape_id;
					state.error = null;

					const allSheets = Object.keys(
						payload.sheet_names
					);
					const previewRange = payload.preview_data_range;
					const selectedSheet = previewRange.sheet_name;
					state.tapeExtraction = {
						sheets: allSheets,
						selectedSheet,
						allSheetsData: Object.entries(
							payload.sheet_names
						).map(([key, value]: any) => {
							return {
								sheetName: key,
								meta: value,
							};
						}),
						suggestedRange: null,
					};
					state.nextStep = payload.next_step;
				}
			)
			.addCase(
				uploadTapeThunk.rejected,
				(state, action) => {
					state.tapeUploadStatus.loading = false;
					state.tapeUploadStatus.status = "failed";
					state.error = action.payload as any;
				}
			);

		builder
			.addCase(editTapeMetaDataThunk.pending, (state) => {
				state.tapeUploadStatus.status = "in-progress";
				state.tapeUploadStatus.loading = true;
				state.error = null;
			})
			.addCase(
				editTapeMetaDataThunk.fulfilled,
				(state, action) => {
					state.tapeUploadStatus.status = "completed";
					state.tapeUploadStatus.loading = false;
				}
			)
			.addCase(
				editTapeMetaDataThunk.rejected,
				(state, action) => {
					state.tapeUploadStatus.loading = false;
					state.tapeUploadStatus.status = "failed";
					state.error = action.payload as any;
				}
			);

		builder
			.addCase(getDataRangeThunk.pending, (state) => {
				state.error = null;
				state.tapeExtractionStatus.loading = true;
				state.tapeExtractionStatus.shortPreviewLoading =
					true;
			})
			.addCase(
				getDataRangeThunk.fulfilled,
				(state, action) => {
					state.error = null;

					const payload = action.payload;

					const preview = action.payload.preview_data_range;

					const suggestion = {
						sheetName: preview.sheet_name,
						headerRange: preview.header_range,
						bodyRange: preview.body_range,
					};

					const allSheets = Object.keys(
						payload.sheet_bounds
					);
					const selectedSheet = suggestion.sheetName;

					const confirmData = payload.confirmed_data_range;
					const isPreviouslyConfirmed =
						Boolean(confirmData);
					const isModifiedAndConfirmed = isEqual(
						confirmData,
						preview
					);

					state.tapeExtraction = {
						sheets: allSheets,
						selectedSheet,
						allSheetsData: Object.entries(
							payload.sheet_bounds
						).map(([key, value]: any) => {
							return {
								sheetName: key,
								meta: value,
							};
						}),
						suggestedRange: suggestion,
						recentRangeSelection: {
							sheet: suggestion.sheetName,
							headerRange: suggestion.headerRange,
							dataRange: suggestion.bodyRange,
						},
						isStepConfirmed: isPreviouslyConfirmed,
						isModifiedNotConfirmed: !isModifiedAndConfirmed,
					};
					state.tapeExtractionStatus.loading = false;
					state.tapeExtractionStatus.shortPreviewLoading =
						false;
				}
			)
			.addCase(
				getDataRangeThunk.rejected,
				(state, action) => {
					state.error = action.payload as string;
					state.tapeExtraction = null;
					state.tapeExtractionStatus.loading = false;
					state.tapeExtractionStatus.error =
						action.payload as string;
					state.tapeExtractionStatus.shortPreviewLoading =
						false;
				}
			);
		builder
			.addCase(getDataFromDataRange.pending, (state) => {
				state.tapeExtractionStatus.loading = true;
				state.error = null;
				state.tapeExtractionStatus.shortPreviewLoading =
					true;
				state.tapeExtractionStatus.error = null;
				state.tapeExtractionStatus.statusCode = undefined;
			})
			.addCase(
				getDataFromDataRange.fulfilled,
				(state, action) => {
					const payload = action.payload;
					state.tapeExtractionStatus.loading = false;
					state.error = null;
					if (state.tapeExtraction && payload.table) {
						state.tapeExtraction.shortPreview =
							payload.table;
					}
					state.tapeExtractionStatus.shortPreviewLoading =
						false;
					state.tapeExtractionStatus.error = null;
					state.tapeExtractionStatus.statusCode = undefined;
				}
			)
			.addCase(
				getDataFromDataRange.rejected,
				(state, action) => {
					state.tapeExtractionStatus.loading = false;
					state.error = action.payload as string;
					state.tapeExtractionStatus.shortPreviewLoading =
						false;
					state.tapeExtractionStatus.error = (
						action.payload as any
					)?.msg as string;

					state.tapeExtractionStatus.statusCode = (
						action.payload as any
					).status;
					if (state.tapeExtraction) {
						state.tapeExtraction.shortPreview = undefined;
					}
				}
			);
		builder
			.addCase(
				getColumnMappingDataThunk.pending,
				(state) => {
					state.columnMappingStatus.loading = true;
					state.error = null;
				}
			)
			.addCase(
				getColumnMappingDataThunk.fulfilled,
				(state, action) => {
					state.columnMappingStatus.loading = false;
					state.error = null;
					if (action.payload) {
						state.columnMapping =
							mapColumnMappingState(action.payload);
					}
				}
			)
			.addCase(
				getColumnMappingDataThunk.rejected,
				(state, action) => {
					state.columnMappingStatus.loading = false;
					state.error = action.payload as string;
				}
			);
		builder
			.addCase(
				updateColumnMappingDataThunk.pending,
				(state) => {
					state.error = null;
					state.columnMappingStatus.previewLoading = true;
				}
			)
			.addCase(
				updateColumnMappingDataThunk.fulfilled,
				(state, action) => {
					state.error = null;
					if (!action.payload.isStepConfirmed) {
						state.columnMapping =
							mapColumnMappingState(
								action.payload.response
							);
					}
					state.columnMappingStatus.previewLoading = false;
				}
			)
			.addCase(
				updateColumnMappingDataThunk.rejected,
				(state, action) => {
					state.columnMappingStatus.previewLoading = false;
					state.error = action.payload as string;
				}
			);
		builder
			.addCase(getStatistics.pending, (state) => {
				state.reviewStatisticsStatus.loading = true;
				state.error = null;
			})
			.addCase(getStatistics.fulfilled, (state, action) => {
				state.reviewStatisticsStatus.loading = false;
				state.error = null;
				if (action.payload) {
					state.reviewStatistics = {
						col_stats: action.payload.col_stats,
						totalLoans: action.payload.total_loans,
					};
				}
			})
			.addCase(getStatistics.rejected, (state, action) => {
				state.reviewStatisticsStatus.loading = false;
				state.error = action.payload as string;
				state.reviewStatisticsStatus.error =
					action.payload as any;
				toast.error("Error occurred while fetching stats.");
			});
		builder
			.addCase(getTapeInfo.pending, (state) => {
				state.tapeSanitizationInfoStatus.loading = true;
			})
			.addCase(getTapeInfo.fulfilled, (state, action) => {
				if (
					action.payload.is_version_supported &&
					action.payload.all_steps &&
					action.payload.current_step
				) {
					const currentStepIndex =
						action.payload.all_steps.findIndex(
							(el: string) =>
								el === action.payload.current_step
						);
					state.tapeSanitizationInfo = {
						allSteps:
							action.payload.all_steps.map(
								(el: string, index: number) => {
									const step = getSanitizationSteps(el);
									return {
										...step,
										isCompleted: currentStepIndex > index,
									};
								}
							) ?? [],
						currentStep: {
							...getSanitizationSteps(
								action.payload.current_step as string
							),
							isCompleted: false,
						},
						tapeName: action.payload.tape_name,
					};
				} else if (
					action.payload.is_version_supported === false
				) {
					state.tapeSanitizationInfo = {
						allSteps: [],
						currentStep: {
							...getSanitizationSteps(
								"SANITIZATION_COMPLETE"
							),
							isCompleted: true,
						},
						tapeName: "",
					};
				}
				state.tapeSanitizationInfoStatus.loading = false;
			})
			.addCase(getTapeInfo.rejected, (state, action) => {
				state.tapeSanitizationInfoStatus.loading = false;
			});
		builder.addCase(
			getPreferencesThunk.pending,
			(state) => {
				state.error = null;
			}
		);
		builder.addCase(
			getPreferencesThunk.fulfilled,
			(state, action) => {
				const payload = action.payload;
				state.preferences = {
					...state.preferences,
					showDataRangeCoachMark: Boolean(
						payload.ui.show_sanitization_tutorial
					),
				};
			}
		);
		builder.addCase(
			getPreferencesThunk.rejected,
			(state, action) => {
				state.error = action.payload as string;
				state.preferences = {
					showDataRangeCoachMark: true,
					showColumnMappingCoachMark: true,
				};
			}
		);
		builder
			.addCase(getSearchMeta.pending, (state) => {
				state.error = null;
			})
			.addCase(getSearchMeta.fulfilled, (state, action) => {
				state.searchMeta = action.payload;
			})
			.addCase(getSearchMeta.rejected, (state, action) => {
				state.error = action.payload as string;
			});

		builder
			.addCase(getCustomColumnsThunk.pending, (state, action) => {
				const requestedTapeId = action.meta.arg;
				const isTapeChanged =
					state.customColumns.tapeId !== requestedTapeId;

				if (isTapeChanged) {
					state.customColumns.columns = [];
					state.customColumns.rawCols = [];
					state.customColumns.rawColsBySource = {};
					state.customColumns.hasFetched = false;
				}

				state.customColumns.tapeId = requestedTapeId;
				state.customColumns.loading = true;
				state.customColumns.error = null;
			})
			.addCase(getCustomColumnsThunk.fulfilled, (state, action) => {
				applyCustomColumnsStateFromResponse(
					state.customColumns,
					action.payload,
					action.meta.arg
				);
			})
			.addCase(getCustomColumnsThunk.rejected, (state, action) => {
				state.customColumns.tapeId = action.meta.arg;
				state.customColumns.hasFetched = false;
				state.customColumns.loading = false;
				state.customColumns.error =
					(action.payload as any)?.msg ??
					"Error loading custom columns";
			});

		builder
			.addCase(addCustomColumnThunk.pending, (state) => {
				state.customColumns.saveLoading = true;
			})
			.addCase(addCustomColumnThunk.fulfilled, (state, action) => {
				applyCustomColumnsStateFromResponse(
					state.customColumns,
					action.payload,
					action.meta.arg.tapeId,
					{
						mergeSingleColumn: true,
						colId: action.meta.arg.colId,
						name: action.meta.arg.name,
					}
				);
				state.customColumns.saveLoading = false;
			})
			.addCase(addCustomColumnThunk.rejected, (state) => {
				state.customColumns.saveLoading = false;
			});

		builder
			.addCase(updateCustomColumnThunk.pending, (state) => {
				state.customColumns.saveLoading = true;
			})
			.addCase(updateCustomColumnThunk.fulfilled, (state, action) => {
				applyCustomColumnsStateFromResponse(
					state.customColumns,
					action.payload,
					action.meta.arg.tapeId,
					{
						mergeSingleColumn: true,
						colId: action.meta.arg.colId,
						name: action.meta.arg.name,
					}
				);
				state.customColumns.saveLoading = false;
			})
			.addCase(updateCustomColumnThunk.rejected, (state) => {
				state.customColumns.saveLoading = false;
			});

		builder
			.addCase(deleteCustomColumnThunk.pending, (state) => {
				state.customColumns.saveLoading = true;
			})
			.addCase(
				deleteCustomColumnThunk.fulfilled,
				(state, action) => {
					const colId = action.payload.colId;
					state.customColumns.columns =
						state.customColumns.columns.filter(
							(c: any) => c.col_id !== colId
						);
					state.customColumns.saveLoading = false;
				}
			)
			.addCase(deleteCustomColumnThunk.rejected, (state) => {
				state.customColumns.saveLoading = false;
			});
	},
});

export const SanitizationActions =
	sanitizationState.actions;
export const SanitizationReducer =
	sanitizationState.reducer;
